import React, { Component } from "react";
import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  getTransportDrivers,
  createTransportDriver,
  updateTransportDriver,
  deleteTransportDriver,
} from "../../Endpoint/Admin/index";
import BranchAssignment from "../../components/Common/BranchAssignment";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/Common/ConfirmModal";
import { canWrite } from "../../services/PermissionService";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, MiniStat, StatRow, SearchPill, Table, Chip, Btn, IconBtn, Avatar, MeterList, MODAL_CSS,
} from "../../components/Console/ConsoleUI";

/**
 * Fleet & drivers — console screen D5 ("Web Transport Fleet").
 *
 * A reskin of the driver registry: the same /transportDriver list, create,
 * update and delete calls, the same BranchAssignment inside the editor, and
 * the same canWrite() gating on every write control.
 *
 * The handoff's right-hand column shows a shift roster and a maintenance
 * schedule. Neither exists in this system — there is no shift, odometer or
 * service-due field on a driver — so rather than draw fiction those panels
 * carry real breakdowns of the same records: the mix of vehicle types on the
 * books, and the drivers whose file is incomplete or switched off. The
 * design's "Shift roster" button is omitted for the same reason.
 */

const CSS = MODAL_CSS;

const VEHICLE_TYPES = {
  sedan: "Sedan",
  suv: "SUV",
  van: "Van",
  bus: "Bus",
  other: "Other",
};

class TransportDrivers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drivers: [],
      isLoaded: false,
      showModal: false,
      isEditing: false,
      // View-only filter. It never changes what is fetched.
      search: "",
      currentDriver: {
        name: "",
        mobile: "",
        email: "",
        vehicleType: "sedan",
        vehicleNumber: "",
        vehicleModel: "",
        notes: "",
        isActive: true,
        branchIds: [],
        excludedBranchIds: [],
      },
      // Delete modal states
      showDeleteModal: false,
      selectedDriverId: null,
      deleteLoading: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Drivers", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadDrivers();
  }

  loadDrivers = () => {
    getTransportDrivers()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ drivers: result.data, isLoaded: true });
        } else {
          this.setState({ isLoaded: true });
          toast.error(result?.message || "Failed to load drivers");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading drivers");
      });
  };

  visibleRows = () => {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return this.state.drivers;
    return this.state.drivers.filter((d) =>
      [
        d.name,
        d.mobile,
        d.email,
        VEHICLE_TYPES[d.vehicleType] || d.vehicleType,
        d.vehicleModel,
        d.vehicleNumber,
        d.isActive ? "Active" : "Inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  };

  columns = () => [
    {
      key: "vehicle",
      label: "Vehicle",
      render: (d) => (
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <span
            className="ms"
            style={{ fontSize: 22, color: C.brown, background: C.iconTint, padding: 8, borderRadius: 10 }}
          >
            directions_car
          </span>
          <div>
            <div style={{ fontSize: 15, color: C.ink }}>
              {d.vehicleModel || VEHICLE_TYPES[d.vehicleType] || d.vehicleType || "—"}
            </div>
            {d.vehicleModel && (
              <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>
                {VEHICLE_TYPES[d.vehicleType] || d.vehicleType}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "plate",
      label: "Plate",
      width: 120,
      render: (d) =>
        d.vehicleNumber ? (
          <span style={{ color: C.slate }}>{d.vehicleNumber}</span>
        ) : (
          <span style={{ color: C.taupeLight }}>not on file</span>
        ),
    },
    {
      key: "driver",
      label: "Driver",
      render: (d) => (
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <Avatar name={d.name} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, color: C.ink }}>{d.name}</div>
            {/* Phone only. The email is searchable and sits in the editor —
                putting it here forces the vehicle column into three lines. */}
            <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>{d.mobile}</div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: 110,
      render: (d) => (
        <Chip tone={d.isActive ? "good" : "neutral"}>{d.isActive ? "Active" : "Inactive"}</Chip>
      ),
    },
    {
      // Trips and rating read as one fact about a driver's record, and pulling
      // them apart costs a column the names need more.
      key: "record",
      label: "Trips",
      width: 110,
      render: (d) => (
        <div>
          <div style={{ color: C.ink }}>{d.totalTrips || 0}</div>
          <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>
            {d.totalRatings > 0 ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span className="ms" style={{ fontSize: 15, color: C.bronze }}>star</span>
                {(d.averageRating || 0).toFixed(1)} ({d.totalRatings})
              </span>
            ) : (
              "no ratings"
            )}
          </div>
        </div>
      ),
    },
    // Write controls are a column only for accounts that may write, matching
    // the gating the previous screen applied to its Actions cell.
    ...(canWrite()
      ? [
          {
            key: "actions",
            label: "",
            align: "right",
            width: 100,
            render: (d) => (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <IconBtn icon="edit" title="Edit driver" onClick={() => this.handleEdit(d)} />
                <IconBtn
                  icon="delete"
                  title="Delete driver"
                  tone="bad"
                  onClick={() => this.handleDelete(d._id)}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  handleAdd = () => {
    this.setState({
      showModal: true,
      isEditing: false,
      currentDriver: {
        name: "",
        mobile: "",
        email: "",
        vehicleType: "sedan",
        vehicleNumber: "",
        vehicleModel: "",
        notes: "",
        isActive: true,
        branchIds: [],
        excludedBranchIds: [],
      },
    });
  };

  handleEdit = (driver) => {
    this.setState({
      showModal: true,
      isEditing: true,
      currentDriver: {
        ...driver,
        branchIds: (driver.branchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
        excludedBranchIds: (driver.excludedBranchIds || []).map((b) =>
          b && b._id ? b._id : String(b)
        ),
      },
    });
  };

  handleSave = () => {
    const { currentDriver, isEditing } = this.state;

    if (!currentDriver.name || !currentDriver.mobile) {
      toast.error("Name and mobile are required");
      return;
    }

    if (isEditing) {
      updateTransportDriver(currentDriver._id, currentDriver)
        .then((response) => {
          if (response && response.status === 200) {
            toast.success("Driver updated successfully");
            this.setState({ showModal: false });
            this.loadDrivers();
          } else {
            toast.error(response?.message || "Failed to update driver");
          }
        })
        .catch(() => toast.error("Error updating driver"));
    } else {
      createTransportDriver(currentDriver)
        .then((response) => {
          // Accept both 200 (existing driver updated) and 201 (new driver created)
          if (response && (response.status === 200 || response.status === 201)) {
            const message = response.isExisting
              ? "Driver updated successfully (existing mobile)"
              : "Driver created successfully";
            toast.success(message);
            this.setState({ showModal: false });
            this.loadDrivers();
          } else {
            toast.error(response?.message || "Failed to create driver");
          }
        })
        .catch(() => toast.error("Error creating driver"));
    }
  };

  // Delete modal toggle
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleDelete = (id) => {
    this.setState({ selectedDriverId: id, showDeleteModal: true });
  };

  confirmDelete = () => {
    const { selectedDriverId } = this.state;
    this.setState({ deleteLoading: true });
    deleteTransportDriver(selectedDriverId)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Driver deleted");
          this.loadDrivers();
        } else {
          toast.error(response?.message || "Failed to delete driver");
        }
      })
      .catch(() => toast.error("Error deleting driver"))
      .finally(() => {
        this.setState({ deleteLoading: false, showDeleteModal: false, selectedDriverId: null });
      });
  };

  updateField = (field, value) => {
    this.setState({
      currentDriver: { ...this.state.currentDriver, [field]: value },
    });
  };

  render() {
    const { drivers, showModal, isEditing, currentDriver, isLoaded, search } = this.state;
    const rows = this.visibleRows();

    const active = drivers.filter((d) => d.isActive).length;
    const trips = drivers.reduce((s, d) => s + Number(d.totalTrips || 0), 0);
    const rated = drivers.filter((d) => d.totalRatings > 0);
    const avgRating = rated.length
      ? (rated.reduce((s, d) => s + Number(d.averageRating || 0), 0) / rated.length).toFixed(1)
      : "—";
    const plated = drivers.filter((d) => d.vehicleNumber).length;

    // Vehicle mix, in the design's meter shape but built from real records.
    const pct = (n) => (drivers.length ? Math.round((n / drivers.length) * 100) : 0);
    const mix = Object.keys(VEHICLE_TYPES)
      .map((k) => ({ label: VEHICLE_TYPES[k], count: drivers.filter((d) => d.vehicleType === k).length }))
      .filter((m) => m.count > 0)
      .sort((a, b) => b.count - a.count);
    const other = drivers.filter((d) => !VEHICLE_TYPES[d.vehicleType]).length;
    if (other) mix.push({ label: "Unclassified", count: other });
    const mixItems = mix.map((m) => ({ label: m.label, value: m.count, pct: pct(m.count) }));

    const incomplete = drivers.filter((d) => !d.vehicleNumber || !d.isActive).slice(0, 5);

    return (
      <ConsolePage
        title="Fleet & drivers"
        subtitle={`Transport · ${drivers.length} driver${drivers.length === 1 ? "" : "s"} on file${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Driver, plate or model"
              width={240}
            />
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/transport/requests")}>
              Requests
            </Btn>
            {canWrite() && (
              <Btn icon="add" onClick={this.handleAdd}>
                Add driver
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style>{CSS}</style>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <MiniStat
              label="Drivers active"
              value={`${active} / ${drivers.length}`}
              hint={`${drivers.length - active} switched off`}
            />
            <MiniStat label="Vehicles with a plate" value={plated} hint={`${drivers.length - plated} missing a plate`} />
            <MiniStat label="Trips logged" value={trips} hint="lifetime, across all drivers" />
            <MiniStat label="Average rating" value={avgRating} hint={`${rated.length} driver${rated.length === 1 ? "" : "s"} rated`} />
          </StatRow>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: 20, alignItems: "start" }}>
            <Panel style={{ padding: 0 }}>
              <div style={{ padding: "0 10px" }}>
                {!isLoaded ? (
                  <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                    Loading drivers…
                  </div>
                ) : (
                  <Table
                    columns={this.columns()}
                    rows={rows}
                    empty="No drivers on file yet"
                  />
                )}
              </div>
            </Panel>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Panel title="Vehicle mix" subtitle="Types on the books">
                <MeterList items={mixItems} empty="Nothing recorded yet." />
              </Panel>

              <Panel title="Needs attention" subtitle="Incomplete or inactive files">
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                  {incomplete.length === 0 ? (
                    <div style={{ fontSize: 14, color: C.taupeLight }}>
                      Every driver is active and has a plate on file.
                    </div>
                  ) : (
                    incomplete.map((d) => (
                      <div
                        key={d._id}
                        style={{
                          padding: "12px 14px",
                          borderRadius: 12,
                          background: !d.isActive ? "#fbe9e9" : "#fbf0dd",
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 500, color: C.brownDeep }}>{d.name}</div>
                        <div style={{ marginTop: 3, fontSize: 13, color: C.taupe }}>
                          {!d.isActive ? "Switched off" : "No vehicle plate recorded"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Panel>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={showModal}
          toggle={() => this.setState({ showModal: false })}
          size="lg"
          className="con-modal"
        >
          <ModalHeader toggle={() => this.setState({ showModal: false })}>
            {isEditing ? "Edit driver" : "Add a driver"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Driver Name *</Label>
                  <Input
                    type="text"
                    value={currentDriver.name}
                    onChange={(e) => this.updateField("name", e.target.value)}
                    placeholder="Enter driver name"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Mobile Number *</Label>
                  <Input
                    type="text"
                    value={currentDriver.mobile}
                    onChange={(e) => this.updateField("mobile", e.target.value)}
                    placeholder="Enter mobile number"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Email (Optional)</Label>
                  <Input
                    type="email"
                    value={currentDriver.email}
                    onChange={(e) => this.updateField("email", e.target.value)}
                    placeholder="Enter email address"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Vehicle Type</Label>
                  <Input
                    type="select"
                    value={currentDriver.vehicleType}
                    onChange={(e) => this.updateField("vehicleType", e.target.value)}
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="van">Van</option>
                    <option value="bus">Bus</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Vehicle Number</Label>
                  <Input
                    type="text"
                    value={currentDriver.vehicleNumber}
                    onChange={(e) => this.updateField("vehicleNumber", e.target.value)}
                    placeholder="e.g., ABC-1234"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Vehicle Model</Label>
                  <Input
                    type="text"
                    value={currentDriver.vehicleModel}
                    onChange={(e) => this.updateField("vehicleModel", e.target.value)}
                    placeholder="e.g., Toyota Camry"
                  />
                </FormGroup>
              </Col>
              <Col xs={12}>
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={currentDriver.notes}
                    onChange={(e) => this.updateField("notes", e.target.value)}
                    placeholder="Any additional notes about the driver"
                  />
                </FormGroup>
              </Col>
              <Col xs={12}>
                <BranchAssignment
                  branchIds={currentDriver.branchIds}
                  excludedBranchIds={currentDriver.excludedBranchIds}
                  overrides={[]}
                  priceFields={[]}
                  onChange={({ branchIds, excludedBranchIds }) =>
                    this.setState((prev) => ({
                      currentDriver: { ...prev.currentDriver, branchIds, excludedBranchIds },
                    }))
                  }
                />
              </Col>
              <Col xs={12}>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={currentDriver.isActive}
                      onChange={(e) => this.updateField("isActive", e.target.checked)}
                    />
                    {" "}Driver is Active
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Btn kind="quiet" onClick={() => this.setState({ showModal: false })}>
              Cancel
            </Btn>
            <Btn onClick={this.handleSave}>
              {isEditing ? "Update driver" : "Add driver"}
            </Btn>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          title="Delete Driver"
          message="Are you sure you want to delete this driver? This action cannot be undone."
          confirmText="Delete"
          confirmColor="danger"
          icon="ri-delete-bin-line"
          loading={this.state.deleteLoading}
        />
      </ConsolePage>
    );
  }
}

export default TransportDrivers;
